"use client";

import type { AppearanceMode } from "@/lib/brand-assets";
import { mercuryLogoAssets } from "@/lib/brand-assets";
import { useAppearanceMode, useMercuryLogoAssets } from "@/lib/use-appearance-mode";
import { SanityMark } from "@/components/sanity-mark";
import "./logo-loader.css";

export type LogoLoaderSize = "sm" | "md" | "lg";
/** `still` = glass circle + aura shadow, no spin (auth / meet mark). */
export type LogoLoaderVariant = "default" | "bare" | "still";

const MARK_PX: Record<LogoLoaderSize, number> = {
  sm: 24,
  md: 28,
  lg: 34,
};

type LogoLoaderProps = {
  size?: LogoLoaderSize;
  className?: string;
  /** Lock logo ink / glass to a canvas appearance (e.g. white boot → light). */
  appearance?: AppearanceMode;
  /** `bare` hides the glass plate (parent already provides a plate). */
  variant?: LogoLoaderVariant;
};

type InnerProps = {
  size: LogoLoaderSize;
  className: string;
  appearance: AppearanceMode;
  variant: LogoLoaderVariant;
  logo: ReturnType<typeof mercuryLogoAssets>;
};

function LogoLoaderInner({
  size,
  className,
  appearance,
  variant,
  logo,
}: InnerProps) {
  const markPx = MARK_PX[size];
  return (
    <span
      className={["logo-loader", className].filter(Boolean).join(" ")}
      data-size={size}
      data-appearance={appearance}
      data-variant={variant}
      aria-hidden="true"
    >
      <span className="logo-loader-breathe">
        <span className="logo-loader-aura" />
        <span className="logo-loader-glass" />
        <span className="logo-loader-spin">
          {/* Inline SVG in the live accent (no raster ink swap). */}
          <SanityMark className="logo-loader-mark" box={markPx} />
        </span>
      </span>
    </span>
  );
}

function LogoLoaderThemed({
  size,
  className,
  variant,
}: {
  size: LogoLoaderSize;
  className: string;
  variant: LogoLoaderVariant;
}) {
  const markPx = MARK_PX[size];
  const appearance = useAppearanceMode();
  const logo = useMercuryLogoAssets(markPx);
  return (
    <LogoLoaderInner
      size={size}
      className={className}
      appearance={appearance}
      variant={variant}
      logo={logo}
    />
  );
}

/**
 * Shared Yatishara logo loader — global spinner for Studio + profile.
 * Plate/aura stay put (breathe); only the mark spins.
 * Aura carries the grow/shrink shadow (clip-path on glass would kill box-shadow).
 * `still` keeps the circle + shadow at rest (sign-in / meet).
 */
export function LogoLoader({
  size = "lg",
  className = "",
  appearance,
  variant = "default",
}: LogoLoaderProps) {
  const markPx = MARK_PX[size];
  if (appearance) {
    return (
      <LogoLoaderInner
        size={size}
        className={className}
        appearance={appearance}
        variant={variant}
        logo={mercuryLogoAssets(markPx, appearance)}
      />
    );
  }
  return (
    <LogoLoaderThemed size={size} className={className} variant={variant} />
  );
}
